import type {
  CapabilityAuditBufferOptions,
  CapabilityBrokerOptions,
} from "./capabilities";
import type { CreateIsolatedRunnerOptions } from "./run";
import type { IsolateOptions, RunOptions } from "./types";

export type IsolatePolicyName =
  | "ai-tool"
  | "tenant-script"
  | "plugin"
  | "trusted";

export type ResolvedIsolatePolicy = {
  name: IsolatePolicyName;
  description: string;
  isolate: Pick<IsolateOptions, "backend" | "harden" | "memoryLimit">;
  run: Required<Pick<RunOptions, "timeout">>;
  recipe: IsolatePolicyRecipe;
  console: {
    capture: "drop" | "host";
  };
  fallback: {
    allowWorker: boolean;
    productionNote: string;
  };
};

export type IsolatePolicyRecipe = {
  audit: {
    maxEvents: number;
  };
  broker: {
    defaultConcurrency: number;
    defaultMaxOutputBytes: number;
    defaultTimeoutMs: number;
  };
  console: {
    maxBytes: number;
    maxEntries: number;
  };
  pool: {
    idleMs: number;
    maxSize: number;
    recycleAfter: number;
  };
  run: Required<Pick<RunOptions, "maxResultBytes" | "timeout">>;
};

export type ResolveIsolatePolicyOverrides = {
  backend?: IsolateOptions["backend"];
  harden?: IsolateOptions["harden"];
  memoryLimit?: IsolateOptions["memoryLimit"];
  timeout?: RunOptions["timeout"];
  maxResultBytes?: RunOptions["maxResultBytes"];
  auditMaxEvents?: number;
  brokerDefaultConcurrency?: number;
  brokerDefaultMaxOutputBytes?: number;
  brokerDefaultTimeoutMs?: number;
  captureConsole?: boolean;
  maxConsoleBytes?: number;
  maxConsoleEntries?: number;
  poolIdleMs?: number;
  poolMaxSize?: number;
  poolRecycleAfter?: number;
  allowWorkerFallback?: boolean;
};

export type PolicyAppliedIsolateOptions = Omit<IsolateOptions, "policy"> & {
  policy?: ResolvedIsolatePolicy;
};

type PolicyOrRecipe = ResolvedIsolatePolicy | IsolatePolicyRecipe;

export type PolicyBrokerRecipeOptions = Pick<
  CapabilityBrokerOptions<unknown>,
  "defaultConcurrency" | "defaultMaxOutputBytes" | "defaultTimeoutMs"
>;

export type PolicyRunnerRecipeOptions = Pick<
  CreateIsolatedRunnerOptions,
  "pool" | "run"
>;

const recipeOf = (policy: PolicyOrRecipe): IsolatePolicyRecipe =>
  "recipe" in policy ? policy.recipe : policy;

const policyDefaults = {
  "ai-tool": {
    console: { capture: "host" },
    description:
      "Model-generated or user-requested code with tight runtime, heap, and audit expectations.",
    fallback: {
      allowWorker: false,
      productionNote:
        "Require FFI in production and compose with an OS boundary when generated code can reach valuable host secrets.",
    },
    isolate: { backend: "ffi", harden: true, memoryLimit: 128 },
    name: "ai-tool",
    recipe: {
      audit: { maxEvents: 100 },
      broker: {
        defaultConcurrency: 8,
        defaultMaxOutputBytes: 64 * 1024,
        defaultTimeoutMs: 250,
      },
      console: { maxBytes: 16 * 1024, maxEntries: 32 },
      pool: { idleMs: 60_000, maxSize: 64, recycleAfter: 1_000 },
      run: { maxResultBytes: 64 * 1024, timeout: 1000 },
    },
    run: { timeout: 1000 },
  },
  "tenant-script": {
    console: { capture: "host" },
    description:
      "Customer-authored workflow, transform, formula, or policy code with explicit host capabilities.",
    fallback: {
      allowWorker: true,
      productionNote:
        "Prefer FFI for hostile tenants; Worker fallback is acceptable for trusted tenants or when an outer process/container boundary owns blast-radius control.",
    },
    isolate: { backend: "auto", harden: true, memoryLimit: 256 },
    name: "tenant-script",
    recipe: {
      audit: { maxEvents: 200 },
      broker: {
        defaultConcurrency: 16,
        defaultMaxOutputBytes: 128 * 1024,
        defaultTimeoutMs: 500,
      },
      console: { maxBytes: 64 * 1024, maxEntries: 64 },
      pool: { idleMs: 120_000, maxSize: 128, recycleAfter: 2_000 },
      run: { maxResultBytes: 256 * 1024, timeout: 5000 },
    },
    run: { timeout: 5000 },
  },
  plugin: {
    console: { capture: "host" },
    description:
      "Third-party plugin code where host powers should be explicit and reviewable.",
    fallback: {
      allowWorker: false,
      productionNote:
        "Require FFI for untrusted plugins; keep package manager, filesystem, network, and process powers behind brokered host tools.",
    },
    isolate: { backend: "ffi", harden: true, memoryLimit: 192 },
    name: "plugin",
    recipe: {
      audit: { maxEvents: 200 },
      broker: {
        defaultConcurrency: 4,
        defaultMaxOutputBytes: 64 * 1024,
        defaultTimeoutMs: 500,
      },
      console: { maxBytes: 32 * 1024, maxEntries: 64 },
      pool: { idleMs: 60_000, maxSize: 64, recycleAfter: 1_000 },
      run: { maxResultBytes: 128 * 1024, timeout: 2000 },
    },
    run: { timeout: 2000 },
  },
  trusted: {
    console: { capture: "drop" },
    description:
      "Trusted application code that needs cancellation, pooling, or heap accounting more than a hostile-code boundary.",
    fallback: {
      allowWorker: true,
      productionNote:
        "Use for trusted code only; hardening is off and host globals may be reachable.",
    },
    isolate: { backend: "auto", harden: false, memoryLimit: 512 },
    name: "trusted",
    recipe: {
      audit: { maxEvents: 100 },
      broker: {
        defaultConcurrency: 64,
        defaultMaxOutputBytes: 512 * 1024,
        defaultTimeoutMs: 5_000,
      },
      console: { maxBytes: 128 * 1024, maxEntries: 128 },
      pool: { idleMs: 300_000, maxSize: 256, recycleAfter: 5_000 },
      run: { maxResultBytes: 1024 * 1024, timeout: 30000 },
    },
    run: { timeout: 30000 },
  },
} as const satisfies Record<IsolatePolicyName, ResolvedIsolatePolicy>;

const clonePolicy = (policy: ResolvedIsolatePolicy): ResolvedIsolatePolicy => ({
  console: { ...policy.console },
  description: policy.description,
  fallback: { ...policy.fallback },
  isolate: { ...policy.isolate },
  name: policy.name,
  recipe: {
    audit: { ...policy.recipe.audit },
    broker: { ...policy.recipe.broker },
    console: { ...policy.recipe.console },
    pool: { ...policy.recipe.pool },
    run: { ...policy.recipe.run },
  },
  run: { ...policy.run },
});

const setIfDefined = <K extends keyof IsolateOptions>(
  target: IsolateOptions,
  key: K,
  value: IsolateOptions[K],
): void => {
  if (value !== undefined) target[key] = value;
};

export const resolveIsolatePolicy = (
  name: IsolatePolicyName,
  overrides: ResolveIsolatePolicyOverrides = {},
): ResolvedIsolatePolicy => {
  const base = clonePolicy(policyDefaults[name]);

  if (overrides.backend !== undefined) base.isolate.backend = overrides.backend;
  if (overrides.harden !== undefined) base.isolate.harden = overrides.harden;
  if (overrides.memoryLimit !== undefined) {
    base.isolate.memoryLimit = overrides.memoryLimit;
  }
  if (overrides.timeout !== undefined) {
    base.run.timeout = overrides.timeout;
    base.recipe.run.timeout = overrides.timeout;
  }
  if (overrides.maxResultBytes !== undefined) {
    base.recipe.run.maxResultBytes = overrides.maxResultBytes;
  }
  if (overrides.auditMaxEvents !== undefined) {
    base.recipe.audit.maxEvents = overrides.auditMaxEvents;
  }
  if (overrides.brokerDefaultConcurrency !== undefined) {
    base.recipe.broker.defaultConcurrency = overrides.brokerDefaultConcurrency;
  }
  if (overrides.brokerDefaultMaxOutputBytes !== undefined) {
    base.recipe.broker.defaultMaxOutputBytes =
      overrides.brokerDefaultMaxOutputBytes;
  }
  if (overrides.brokerDefaultTimeoutMs !== undefined) {
    base.recipe.broker.defaultTimeoutMs = overrides.brokerDefaultTimeoutMs;
  }
  if (overrides.captureConsole !== undefined) {
    base.console.capture = overrides.captureConsole ? "host" : "drop";
  }
  if (overrides.maxConsoleBytes !== undefined) {
    base.recipe.console.maxBytes = overrides.maxConsoleBytes;
  }
  if (overrides.maxConsoleEntries !== undefined) {
    base.recipe.console.maxEntries = overrides.maxConsoleEntries;
  }
  if (overrides.poolIdleMs !== undefined) {
    base.recipe.pool.idleMs = overrides.poolIdleMs;
  }
  if (overrides.poolMaxSize !== undefined) {
    base.recipe.pool.maxSize = overrides.poolMaxSize;
  }
  if (overrides.poolRecycleAfter !== undefined) {
    base.recipe.pool.recycleAfter = overrides.poolRecycleAfter;
  }
  if (overrides.allowWorkerFallback !== undefined) {
    base.fallback.allowWorker = overrides.allowWorkerFallback;
  }

  return base;
};

export const policyAuditOptions = (
  policy: PolicyOrRecipe,
): CapabilityAuditBufferOptions => ({
  maxEvents: recipeOf(policy).audit.maxEvents,
});

export const policyBrokerOptions = (
  policy: PolicyOrRecipe,
): PolicyBrokerRecipeOptions => {
  const recipe = recipeOf(policy);
  return { ...recipe.broker };
};

export const policyConsoleOptions = (
  policy: PolicyOrRecipe,
): Pick<IsolateOptions, "maxConsoleBytes" | "maxConsoleEntries"> => {
  const recipe = recipeOf(policy);
  return {
    maxConsoleBytes: recipe.console.maxBytes,
    maxConsoleEntries: recipe.console.maxEntries,
  };
};

export const policyRunOptions = (
  policy: PolicyOrRecipe,
): Required<Pick<RunOptions, "maxResultBytes" | "timeout">> => ({
  ...recipeOf(policy).run,
});

export const policyRunnerOptions = (
  policy: PolicyOrRecipe,
): PolicyRunnerRecipeOptions => {
  const recipe = recipeOf(policy);
  return {
    pool: { ...recipe.pool },
    run: {
      maxResultBytes: recipe.run.maxResultBytes,
      timeout: recipe.run.timeout,
    },
  };
};

export const applyIsolatePolicyOptions = (
  options: IsolateOptions = {},
): PolicyAppliedIsolateOptions => {
  const { policy, defaultRunOptions, ...explicit } = options;
  if (policy === undefined) {
    return {
      ...explicit,
      defaultRunOptions:
        defaultRunOptions === undefined ? undefined : { ...defaultRunOptions },
    };
  }

  const resolved =
    typeof policy === "string"
      ? resolveIsolatePolicy(policy)
      : clonePolicy(policy);
  const applied: PolicyAppliedIsolateOptions = {
    defaultRunOptions: {
      ...resolved.recipe.run,
      ...defaultRunOptions,
    },
    policy: resolved,
  };

  setIfDefined(applied, "backend", resolved.isolate.backend);
  setIfDefined(applied, "harden", resolved.isolate.harden);
  setIfDefined(applied, "memoryLimit", resolved.isolate.memoryLimit);

  setIfDefined(applied, "backend", explicit.backend);
  setIfDefined(applied, "harden", explicit.harden);
  setIfDefined(applied, "memoryLimit", explicit.memoryLimit);
  setIfDefined(applied, "bootstrap", explicit.bootstrap);
  setIfDefined(applied, "maxConsoleBytes", explicit.maxConsoleBytes);
  setIfDefined(applied, "maxConsoleEntries", explicit.maxConsoleEntries);
  setIfDefined(applied, "onConsole", explicit.onConsole);
  setIfDefined(
    applied,
    "unsafelyExposeGlobals",
    explicit.unsafelyExposeGlobals,
  );

  return applied;
};
